import type { CloudChatHistoryItem, ConnectionData, ConnectionType, ProjectType } from '~/services/interfaceService';
import * as Sentry from '@sentry/remix';
import type { UIMessage } from 'ai';
import { fetchAndProcessProjects, storageStatusStore } from '~/lib/stores/storageStatusStore';
import type { StoreFileAction } from '~/types/actions';
import { v4 as uuidv4 } from 'uuid';

export const handleCodeImport = (
  e: React.ChangeEvent<HTMLInputElement>,
  codeFiles: Array<{ name: string; content: string }>,
  onCodeFilesUpdate: (files: Array<{ name: string; content: string }>) => void,
) => {
  const items = e.target.files;

  if (items) {
    const newFiles: Array<{ name: string; content: string }> = [];

    const readFile = (file: File) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result as string;
        newFiles.push({ name: file.name, content });

        if (newFiles.length === items.length) {
          // create a map of existing files for quick lookup
          const existingFilesMap = new Map(codeFiles.map((file) => [file.name, file]));

          // process new files
          const updatedFiles = newFiles.reduce(
            (acc, newFile) => {
              // if file with same name exists, update its content
              if (existingFilesMap.has(newFile.name)) {
                return acc.map((file) => (file.name === newFile.name ? { ...file, content: newFile.content } : file));
              }

              // if file doesn't exist, add it to the array
              return [...acc, newFile];
            },
            [...codeFiles],
          );

          onCodeFilesUpdate(updatedFiles);
        }
      };
      reader.readAsText(file);
    };

    Array.from(items).forEach((file) => {
      readFile(file);
    });
  }
};

export const handleThemeImport = (
  e: React.ChangeEvent<HTMLInputElement>,
  onThemeFileUpdate: (file: { name: string; content: string }) => void,
) => {
  const file = e.target.files?.[0];

  if (file) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;
      const themeFile = { name: file.name, content };
      onThemeFileUpdate(themeFile);
    };
    reader.readAsText(file);
  }
};

export const updateProjectConnection = async (projectId: string, connectionData: ConnectionData) => {
  try {
    const response = await fetch('/api/connections', {
      method: 'POST',
      body: JSON.stringify({ projectId, connectionData }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error updating ${connectionData.connectionType} connection`);
    }
  } catch (error) {
    console.error('[updateProjectConnection] Error updating project connection:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error updating project connection',
        projectId,
        connectionData,
      },
    });
  }
};

export const deleteProjectConnection = async (projectId: string, connectionType: ConnectionType) => {
  try {
    const response = await fetch('/api/connections', {
      method: 'DELETE',
      body: JSON.stringify({ projectId, connectionType }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error deleting ${connectionType} connection`);
    }
  } catch (error: any) {
    console.error('[deleteProjectConnection] Error deleting project connection:', error);

    Sentry.captureException(error, {
      extra: {
        message: 'Error deleting project connection',
        projectId,
        connectionType,
      },
    });
    throw new Error(error);
  }
};

export const updateIsGitStatus = async (projectId: string, isGit: boolean) => {
  try {
    const response = await fetch('/api/projects', {
      method: 'PUT',
      body: JSON.stringify({ projectId, isGit, action: 'updateIsGitStatus' }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseData = (await response.json()) as {
      ok: boolean;
      message: string;
      error: string;
    };

    if (!responseData.ok) {
      throw new Error(responseData.error);
    }
  } catch (error) {
    console.error('[updateIsGitStatus] Error updating is git status:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error updating is git status',
        projectId,
        isGit,
      },
    });
    throw new Error('Error updating is git status');
  }
};

export const updateLastOpenedOn = async (projectId: string, lastOpenedOn: string) => {
  try {
    const response = await fetch('/api/projects', {
      method: 'PUT',
      body: JSON.stringify({ projectId, lastOpenedOn, action: 'updateLastOpenedOn' }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseData = (await response.json()) as {
      ok: boolean;
      message: string;
      error: string;
    };

    if (!responseData.ok) {
      throw new Error(responseData.error);
    }
  } catch (error) {
    console.error('[updateIsGitStatus] Error updating is git status:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error updating last opened on',
        projectId,
        lastOpenedOn,
      },
    });
    throw new Error('Error updating last opened on');
  }
};

export const updateProjectType = async (projectId: string, projectType: ProjectType) => {
  try {
    const response = await fetch('/api/projects', {
      method: 'PUT',
      body: JSON.stringify({ projectId, projectType, action: 'updateProjectType' }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseData = (await response.json()) as {
      ok: boolean;
      message: string;
      error: string;
    };

    if (!responseData.ok) {
      throw new Error(responseData.error);
    }
  } catch (error) {
    console.error('[updateProjectType] Error updating project type:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error updating project type',
        projectId,
        projectType,
      },
    });
    throw new Error('Error updating project type');
  }
};

export const fetchFromCloudAPI = async (projectId: string): Promise<any> => {
  try {
    const response = await fetch(`/api/cloudserver?projectId=${projectId}`, {
      method: 'GET',
    });

    const resData = (await response.json()) as {
      fileActions: StoreFileAction[];
      projectData: Partial<CloudChatHistoryItem>;
      messages: UIMessage[];
    };

    // Fetch binary file contents in parallel using their hashes
    try {
      const binaryIndexes: number[] = [];
      const binaryFetches: Promise<Blob>[] = [];

      (resData.fileActions || []).forEach((fa: any, idx: number) => {
        if (fa && fa.isBinary === true && (!fa.content || fa.content === '')) {
          if (fa.contentHash) {
            console.log('[chat.$projectId.$id] binary fetch for:', fa.contentHash, 'filePath:', fa.filePath);
            binaryIndexes.push(idx);
            binaryFetches.push(
              fetch(`/api/file-actions/object/${fa.contentHash}`).then((r) => {
                if (!r.ok) {
                  throw new Error(`blob fetch failed: ${fa.filePath}`);
                }

                return r.blob();
              }),
            );
          }
        }
      });

      if (binaryFetches.length > 0) {
        const results = await Promise.allSettled(binaryFetches);
        results.forEach((res, i) => {
          const idx = binaryIndexes[i];

          if (res.status === 'fulfilled') {
            (resData.fileActions as any)[idx].content = res.value;
            console.log(
              '[chat.$projectId.$id] binary fetch successful for:',
              (resData.fileActions as any)[idx]?.filePath,
              'content:',
              (resData.fileActions as any)[idx].content,
            );
          } else {
            // leave content empty on failure
            console.warn(
              '[chat.$projectId.$id] binary fetch failed:',
              (resData.fileActions as any)[idx]?.filePath,
              res.reason,
            );
            throw new Error(`binary fetch failed: ${(resData.fileActions as any)[idx]?.filePath}`);
          }
        });
      }
    } catch (e) {
      console.warn('[chat.$projectId.$id] parallel blob fetch error:', e);
      throw new Error(`parallel blob fetch error: ${e}`);
    }

    if (response.ok) {
      return resData;
    }

    throw new Error(`API returned ${response.status}`);
  } catch (error) {
    Sentry.captureException(`Error from fetchFromCloudAPI in helper.ts: ${error}`, {
      extra: {
        projectId,
      },
    });
    throw error;
  }
};

export const assignCreatedAtTimestamps = (messages: UIMessage[]) => {
  const N = messages.length;

  // Find the first message with a valid createdAt timestamp
  const baseIdx = messages.findIndex((msg) => !!msg.createdAt);
  let baseTimestamp: number;

  if (baseIdx >= 0) {
    // If found, use its timestamp as base
    baseTimestamp = new Date(messages[baseIdx].createdAt as unknown as string).getTime() - baseIdx * 1000;

    // Assign only up to baseIdx if missing
    for (let i = 0; i < baseIdx; i++) {
      if (!messages[i].createdAt) {
        messages[i].createdAt = new Date(baseTimestamp + i * 1000);
        messages[i].id = uuidv4();
      }
    }
  } else {
    // No message has createdAt, use now
    baseTimestamp = Date.now();

    // Assign for all messages
    for (let i = 0; i < N; i++) {
      if (!messages[i].createdAt) {
        messages[i].createdAt = new Date(baseTimestamp + i * 1000);
        messages[i].id = uuidv4();
      }
    }
  }
};

export async function addProjectToStore(project: CloudChatHistoryItem) {
  try {
    const currentState = storageStatusStore.get();
    const updatedProjects = [project, ...currentState.allProjects];
    await fetchAndProcessProjects(updatedProjects);
    console.log('[updateProjectInStore] New project added to sidebar:', project.projectId);
  } catch (error) {
    console.error('[updateProjectInStore] Error updating store:', error);
  }
}

export async function updateProjectInStore(pId: string, toUpdate: Partial<CloudChatHistoryItem>) {
  try {
    const currentState = storageStatusStore.get();
    const updatedProjects = currentState.allProjects.map((project) =>
      project.projectId === pId ? { ...project, ...toUpdate } : project,
    );
    await fetchAndProcessProjects(updatedProjects);
    console.log('[updateProjectInStore] Project updated in sidebar:', pId);
  } catch (error) {
    console.error('[updateProjectInStore] Error updating store:', error);
  }
}
